import { Card, Chip, Stack, Typography } from "@mui/material";
import { DragEvent, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { palette } from "theme";
import { DOCUMENT_ACCEPT, MAX_GENERATION_DOCUMENTS } from "../../constants";
import { CompetencyMapGenerationFormValues } from "../../types";
import DocumentsUpload from "../DocumentsUpload";

const acceptedDocuments = DOCUMENT_ACCEPT.split(",").map((item) => item.trim().toLowerCase());
const acceptedDocumentExtensions = acceptedDocuments.filter((item) => item.startsWith("."));
const acceptedDocumentMimeTypes = acceptedDocuments.filter((item) => !item.startsWith("."));

const isAcceptedDocument = (file: File) => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return (
    acceptedDocumentMimeTypes.includes(fileType) ||
    acceptedDocumentExtensions.some((extension) => fileName.endsWith(extension))
  );
};

const DocumentsBlock = () => {
  const form = useFormContext<CompetencyMapGenerationFormValues>();
  const { watch, clearErrors, setError, setValue } = form;
  const files = watch("files") || [];
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!Array.from(event.dataTransfer.types).includes("Files")) {
      return;
    }

    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const { dataTransfer } = event;

    if (!Array.from(dataTransfer.types).includes("Files")) {
      return;
    }

    dataTransfer.dropEffect = "copy";
    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!Array.from(event.dataTransfer.types).includes("Files")) {
      return;
    }

    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    dragDepthRef.current = 0;
    setIsDragActive(false);

    const droppedFiles = Array.from(event.dataTransfer.files || []);

    if (!droppedFiles.length) {
      return;
    }

    if (droppedFiles.some((file) => !isAcceptedDocument(file))) {
      setError("files", {
        type: "manual",
        message: "Поддерживаются только файлы txt, md, docx, json и pdf",
      });
      return;
    }

    const nextFiles = [...files, ...droppedFiles].filter(
      (file, index, fileList) =>
        fileList.findIndex(
          (currentFile) => currentFile.name === file.name && currentFile.lastModified === file.lastModified
        ) === index
    );

    if (nextFiles.length > MAX_GENERATION_DOCUMENTS) {
      setError("files", {
        type: "manual",
        message: `Можно загрузить не более ${MAX_GENERATION_DOCUMENTS} файлов`,
      });
      return;
    }

    clearErrors("files");
    setValue("files", nextFiles, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <Card
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        p: 1.5,
        height: "100%",
        border: "1px dashed",
        borderColor: isDragActive ? palette.primary.main : "divider",
        backgroundColor: isDragActive ? "action.hover" : undefined,
        transition: "border-color 0.2s ease, background-color 0.2s ease",
      }}>
      <Stack
        spacing={1.5}
        useFlexGap
        sx={{ height: "100%" }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'>
          <Typography variant='h6'>Документы</Typography>
          <Chip
            label={`${files.length} / ${MAX_GENERATION_DOCUMENTS}`}
            sx={{ backgroundColor: palette.third.main, color: palette.primary.main, "& span": { fontWeight: 600 } }}
            size='small'
          />
        </Stack>
        <DocumentsUpload />
      </Stack>
    </Card>
  );
};

export default DocumentsBlock;
